import React, { useState, useEffect } from "react";
import { FileText, Calendar, CheckCircle, Edit, Trash2, XCircle, Eye } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import LocalDateTime from "services/date-converter";
import { useAlert } from "context/AlertContext";
import { useTheme } from "context/ThemeContext";
import { getAccentColor } from "utils/accent-color";
import apiKnowledgeArticle from "../api";
import { getStatusMeta } from "../article-builder/blocks.data";
import ArticlePreviewDialog from "./ArticlePreviewDialog";
import { articleFilterFields, loadArticleFilterDependencies } from "../features/article.filter";

const EMPTY_FILTERS = {};

const ArticleList = ({ onCreate, onEdit, refreshSignal = 0, onTotalCountChange }) => {
  const { showAlert } = useAlert();
  const { isDark } = useTheme();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [resetSelectionTrigger, setResetSelectionTrigger] = useState(0);
  const [previewArticleId, setPreviewArticleId] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [confirmData, setConfirmData] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
    icon: null,
    resolve: null,
  });

  // Same accent tokens the reader-facing knowledge base uses, so a chapter
  // looks identical here and in the hub. Null when the chapter has no colour
  // set — the row then keeps its neutral styling rather than borrowing a
  // default that would read as "same chapter" next to real colours.
  const getRowAccent = (row) =>
    row.knowledge_base?.color_code ? getAccentColor(row.knowledge_base.color_code, isDark) : null;

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await apiKnowledgeArticle.getAll({
        searchText,
        search: JSON.stringify(filters),
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
      });
      if (response.data.status) {
        setData(response.data.data);
        setTotalCount(response.data.total_count || 0);
        onTotalCountChange?.(response.data.total_count || 0);
      }
    } catch (error) {
      showAlert({ type: "error", message: "An unexpected error occurred. Please try again!" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchText, filters, refreshSignal, resetTrigger]);

  const openConfirm = (options) =>
    new Promise((resolve) => {
      setConfirmData({ ...options, isOpen: true, resolve });
    });

  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData((prev) => ({ ...prev, isOpen: false, resolve: null }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const hasActiveQuery = !!searchText || Object.keys(filters).length > 0;

  const handleApplyFilters = ({ search }) => {
    setFilters(search);
    setCurrentPage(1);
  };

  const handlePageChange = (page, newPageSize = pageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleRefresh = () => {
    setSearchText("");
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
    // Bumping resetTrigger refetches through the effect, after the cleared
    // search and filters are in state — a direct fetch here would still send the old ones.
    setResetTrigger((prev) => prev + 1);
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setResetSelectionTrigger((prev) => prev + 1);
  };

  const handleDeleteSelected = async (ids = []) => {
    if (ids.length === 0) {
      showAlert({ type: "warning", message: "Please select at least one article to delete." });
      return;
    }
    const confirmed = await openConfirm({
      title: "Delete Article",
      message:
        ids.length > 1
          ? `Are you sure you want to delete ${ids.length} selected articles? This action cannot be undone.`
          : "Are you sure you want to delete this article? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <Trash2 size={24} className="text-danger" />,
    });

    if (!confirmed) return;
    try {
      const response = await apiKnowledgeArticle.delete(ids);
      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message });
        fetchArticles();
        handleClearSelection();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "An unexpected error occurred. Please try again!",
      });
    }
  };

  const handleToggleStatus = async (row) => {
    const confirmed = await openConfirm({
      title: `${row.is_activated ? "Deactivate" : "Activate"} Article`,
      message: `Are you sure you want to ${row.is_activated ? "deactivate" : "activate"} "${row.title}"?`,
      confirmText: "Save Changes",
      cancelText: "Cancel",
      icon: <CheckCircle size={24} className="text-success" />,
    });

    if (!confirmed) return;
    const response = await apiKnowledgeArticle.update(row.id, { is_activated: !row.is_activated });
    if (response.data.status) {
      showAlert({
        type: "success",
        message: `Article ${row.is_activated ? "deactivated" : "activated"} successfully`,
      });
      fetchArticles();
    }
  };

  const columns = [
    {
      key: "order",
      label: "Order",
      width: "80px",
      render: (row) => (
        <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-1 rounded-md text-xs font-semibold bg-surface_alt text-text_secondary">
          {row.order ?? "—"}
        </span>
      ),
      sortable: true,
    },
    {
      key: "title",
      label: "Article",
      width: "300px",
      render: (row) => {
        const accent = getRowAccent(row);
        return (
          <div className="flex items-center gap-2.5">
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary_tint flex items-center justify-center"
              style={accent ? { backgroundColor: accent.fill } : undefined}
            >
              <FileText size={15} className="text-primary" style={accent ? { color: accent.text } : undefined} />
            </div>
            <span className="font-medium text-text_primary">{row.title}</span>
          </div>
        );
      },
      sortable: true,
    },
    {
      key: "knowledge_base",
      label: "Knowledge Base",
      width: "240px",
      render: (row) => {
        if (!row.knowledge_base?.title) return <span className="text-text_muted">—</span>;
        const accent = getRowAccent(row);
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold ${
              accent ? "" : "bg-surface_alt text-text_secondary"
            }`}
            style={accent ? { backgroundColor: accent.fill, color: accent.text } : undefined}
          >
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: accent ? accent.base : "currentColor" }}
            />
            {row.knowledge_base.title}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Publish Status",
      width: "150px",
      render: (row) => {
        const meta = getStatusMeta(row.status);
        return (
          <div className="flex flex-col gap-0.5">
            <span className={`w-fit px-3 py-1 rounded-full text-xs font-semibold border ${meta.classes}`}>
              {meta.label}
            </span>
            {row.status === "SCHEDULED" && row.scheduled_at && (
              <span className="text-[10.5px] text-text_muted flex items-center gap-1">
                <Calendar size={10} />
                <LocalDateTime utcTime={row.scheduled_at} type="datetime" />
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "is_activated",
      label: "Active",
      width: "100px",
      render: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${
            row.is_activated
              ? "bg-success_bg text-success border border-success_border"
              : "bg-danger_bg text-danger border border-danger_border"
          }`}
        >
          {row.is_activated ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "updated_date",
      label: "Last Update",
      width: "160px",
      render: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-text_primary">
          <Calendar size={12} className="text-text_muted" />
          <LocalDateTime utcTime={row.updated_date} type="datetime" />
        </div>
      ),
      sortable: true,
    },
  ];

  const renderRowActions = (row) => (
    <div className="flex items-center gap-1.5 justify-end">
      <button
        className="p-1.5 rounded-lg bg-primary_tint text-primary"
        onClick={(e) => {
          e.stopPropagation();
          setPreviewArticleId(row.id);
        }}
        title="Preview Article"
      >
        <Eye size={16} />
      </button>
      <button
        className="p-1.5 rounded-lg bg-info_bg text-info"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(row);
        }}
        title="Edit Article"
      >
        <Edit size={16} />
      </button>
      <button
        className="p-1.5 rounded-lg bg-danger_bg text-danger"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteSelected([row.id]);
        }}
        title="Delete Article"
      >
        <Trash2 size={16} />
      </button>
      <button
        className={`p-1.5 rounded-lg ${
          row.is_activated ? "bg-danger_bg text-danger" : "bg-success_bg text-success"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        title={row.is_activated ? "Active - Click to Deactivate" : "Inactive - Click to Activate"}
      >
        {row.is_activated ? <XCircle size={18} strokeWidth={2.5} /> : <CheckCircle size={18} strokeWidth={2.5} />}
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col gap-4">

      <TableToolBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onCreate={onCreate}
        onDeleteSelected={() => handleDeleteSelected(selectedRows)}
        selectedCount={selectedRows.length}
        resetSearchTrigger={resetTrigger}
        resultCount={totalCount}
        onClearSelection={handleClearSelection}
        advanceFilter={
          <GlobalAdvanceFilter
            fields={articleFilterFields}
            dependenciesLoader={loadArticleFilterDependencies}
            onApply={handleApplyFilters}
            resetKey={resetTrigger}
          />
        }
      />

      {data?.length === 0 && !loading ? (
        <div className="flex-1 flex items-center justify-center border rounded-lg border-border bg-surface">
          <NoDataState
            icon="file"
            title="No Articles Found"
            message={
              hasActiveQuery
                ? "No articles match your search or filters."
                : "There are no knowledge articles available yet."
            }
            showRefresh={false}
            showBack={false}
            showCreate={true}
            createText="Create New Article"
            onCreate={onCreate}
            size="medium"
          />
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-hidden border rounded-lg border-border">
            <TableGrid
              data={data}
              totalCount={totalCount}
              loading={loading}
              currentPage={currentPage}
              columns={columns}
              onSelectionChange={setSelectedRows}
              pageSize={pageSize}
              selectable={true}
              renderActions={renderRowActions}
              resetSelectionTrigger={resetSelectionTrigger}
              onTitleClick={onEdit}
            />
          </div>

          <Pagination
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />

          {confirmData.isOpen && (
            <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
          )}
        </>
      )}

      <ArticlePreviewDialog
        articleId={previewArticleId}
        isOpen={!!previewArticleId}
        onClose={() => setPreviewArticleId(null)}
      />
    </div>
  );
};

export default ArticleList;
